import { addBreadcrumb, logError } from './sentry';

export interface RetryOptions {
  maxAttempts?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  maxDelayMs?: number;
  shouldRetry?: (error: any) => boolean;
  onRetry?: (attempt: number, error: any) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  delayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 10000,
  shouldRetry: (error: any) => {
    // Retry on network errors or 5xx server errors
    if (error?.code === 'unavailable' || error?.code === 'internal') {
      return true;
    }

    // Retry on Firebase timeout errors
    if (error?.code === 'deadline-exceeded') {
      return true;
    }

    // Don't retry on auth errors or client errors
    if (
      error?.code === 'unauthenticated' ||
      error?.code === 'permission-denied' ||
      error?.code === 'invalid-argument' ||
      error?.code === 'not-found'
    ) {
      return false;
    }

    // Retry on generic network errors
    return error?.message?.toLowerCase().includes('network');
  },
  onRetry: () => {},
};

/**
 * Retry a function with exponential backoff
 *
 * @param fn - The function to retry
 * @param options - Retry options
 * @returns The result of the function
 *
 * @example
 * const data = await retryWithBackoff(
 *   () => createSurvey(surveyData),
 *   { maxAttempts: 3, delayMs: 1000 }
 * );
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  let lastError: any;
  let delay = opts.delayMs;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      // Add breadcrumb for tracking
      addBreadcrumb(
        `Attempting request (attempt ${attempt}/${opts.maxAttempts})`,
        'retry',
        { attempt, maxAttempts: opts.maxAttempts }
      );

      const result = await fn();

      // Success - return result
      if (attempt > 1) {
        addBreadcrumb(
          `Request succeeded on attempt ${attempt}`,
          'retry',
          { attempt }
        );
      }

      return result;
    } catch (error: any) {
      lastError = error;

      // Check if we should retry
      const shouldRetry = opts.shouldRetry(error);
      const isLastAttempt = attempt === opts.maxAttempts;

      if (!shouldRetry || isLastAttempt) {
        // Don't retry - throw the error
        addBreadcrumb(
          `Request failed permanently${!shouldRetry ? ' (not retryable)' : ''}`,
          'retry',
          { attempt, error: error.message, shouldRetry }
        );

        if (isLastAttempt) {
          logError(error, {
            context: 'retryWithBackoff',
            attempts: attempt,
            finalAttempt: true,
          });
        }

        throw error;
      }

      // Wait before retrying
      addBreadcrumb(
        `Request failed, retrying in ${delay}ms`,
        'retry',
        { attempt, error: error.message, delayMs: delay }
      );

      opts.onRetry(attempt, error);
      await sleep(delay);

      // Exponential backoff with max delay
      delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelayMs);
    }
  }

  // This should never be reached, but TypeScript needs it
  throw lastError;
}

/**
 * Sleep for a specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: any): boolean {
  return DEFAULT_OPTIONS.shouldRetry(error);
}

/**
 * Retry a Firebase callable function
 * This is a specialized version of retryWithBackoff for Firebase functions
 */
export async function retryFirebaseCall<T>(
  fn: () => Promise<T>,
  functionName: string,
  options: RetryOptions = {}
): Promise<T> {
  return retryWithBackoff(fn, {
    ...options,
    onRetry: (attempt, error) => {
      console.warn(
        `Retrying Firebase function "${functionName}" (attempt ${attempt})`,
        error
      );
      options.onRetry?.(attempt, error);
    },
  });
}
